import "./TocActionsMenu.scss";

import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const MENU_GAP_PX = 4;

export type TocAction = {
  label: string;
  icon?: string;
  onSelect: () => void;
  disabledReason?: string | null;
};

type TocActionsMenuProps = {
  actions: TocAction[];
  disabled?: boolean;
};

const TocActionsMenu = ({ actions, disabled = false }: TocActionsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    menuRef.current
      ?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')
      ?.focus({ preventScroll: true });

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !menuRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current
          ?.querySelector("button")
          ?.focus({ preventScroll: true });
      }
    };
    const handleScroll = (event: Event) => {
      const target = event.target as Node;
      if (triggerRef.current && target.contains(triggerRef.current)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + MENU_GAP_PX,
        right: window.innerWidth - rect.right,
      });
    }
    setIsOpen((current) => !current);
  };

  const handleSelect = (action: TocAction) => {
    setIsOpen(false);
    action.onSelect();
  };

  return (
    <>
      <span ref={triggerRef} className="toc-actions-trigger">
        <ButtonIcon
          size="small"
          title="Actions"
          aria-label="Actions"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={handleToggle}
          disabled={disabled}
        >
          more_vert
        </ButtonIcon>
      </span>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="toc-actions-menu"
            role="menu"
            style={{
              top: `${menuPosition.top}px`,
              right: `${menuPosition.right}px`,
            }}
          >
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                className="toc-actions-menu__item"
                role="menuitem"
                onClick={() => handleSelect(action)}
                disabled={Boolean(action.disabledReason)}
              >
                {action.icon && (
                  <span
                    className="material-icons toc-actions-menu__icon"
                    aria-hidden="true"
                  >
                    {action.icon}
                  </span>
                )}
                <span className="toc-actions-menu__text">
                  <span className="toc-actions-menu__label">
                    {action.label}
                  </span>
                  {action.disabledReason && (
                    <span className="toc-actions-menu__hint">
                      {action.disabledReason}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

export default TocActionsMenu;
